import styled from "styled-components";
import {
  Section,
  Spacer,
  triggerToast,
  Avatar,
  ButtonDS,
  InputDS,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { loadCompanyAsync, selectCompany, selectManager } from "./../homeSlice";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  UsersSlice,
  selectUsersPasswordStatus,
  selectUsersStatus,
  updateUsersAsync,
  updateUsersPasswordAsync,
} from "./usersSlice";
import { UsersDatasAPI, UsersPasswordsAPI } from "./usersAPI";
import { useParams } from "react-router-dom";
import {
  FormPassword,
  UpdatePasswordModal,
} from "./modals/updatePasswordModal";
import {
  companiesSlice,
  selectUpdateCompanyStatusStatus,
  updateCompanyStatusAsync,
} from "../companies/companiesSlice";
import "../../../i18n/i18n";

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
};

export const AccountTab = () => {
  const { t } = useTranslation();
  const manager = useAppSelector(selectManager);
  const params = useParams();
  const updateUserStatus = useAppSelector(selectUsersStatus);
  const updateCompanyStatus = useAppSelector(selectUpdateCompanyStatusStatus);
  const passwordStatus = useAppSelector(selectUsersPasswordStatus);
  const dispatch = useAppDispatch();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCurrentPasswordCorrect, setIsCurrentPasswordCorrect] =
    useState(true);
  const [isNewPasswordMatching, setIsNewPasswordMatching] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const { handleSubmit, formState, register } = useForm<FormData>();

  const company = useAppSelector(selectCompany);

  const isCompanyDeleted = useMemo(
    () => company?.status === "DELETE",
    [company],
  );

  const isManagerOwner = useMemo(() => manager?.role === "OWNER", [manager]);

  useEffect(() => {
    formState.isValid ? setButtonDisabled(false) : setButtonDisabled(true);
  }, [formState]);

  const onSubmitPersonalData: SubmitHandler<FormData> = useCallback(
    (data) => {
      if (!buttonDisabled) {
        const personalInfoData: UsersDatasAPI = {};
        if (data.firstName) personalInfoData.firstName = data.firstName;
        if (data.lastName) personalInfoData.lastName = data.lastName;
        if (data.email) personalInfoData.email = data.email;
        void dispatch(updateUsersAsync(personalInfoData));
      }
    },
    [buttonDisabled, dispatch],
  );

  useEffect(() => {
    if (updateUserStatus === "success") {
      {
        triggerToast(t("users.personalInfo.success") || "", "valid");
        dispatch(UsersSlice.actions.resetUpdateUserStatus());
      }
    } else if (updateUserStatus === "failed") {
      triggerToast(t("users.personalInfo.failed") || "", "error");
      dispatch(UsersSlice.actions.resetUpdateUserStatus());
    }
  }, [dispatch, updateUserStatus, t]);

  useEffect(() => {
    if (!company) return;
    if (updateCompanyStatus === "success") {
      triggerToast(
        t(`users.updateCompanyStatus.success.${company.status}`) || "",
        "valid",
      );
      void dispatch(companiesSlice.actions.resetUpdateCompanyStatusStatus());
      void dispatch(loadCompanyAsync({ uuid: company.uuid }));
      triggerToast(t("users.updateCompanyStatus.failed") || "", "error");
    } else if (updateCompanyStatus === "failed") {
      triggerToast(t("users.updateCompanyStatus.failed") || "", "error");
      void dispatch(companiesSlice.actions.resetUpdateCompanyStatusStatus());
    }
  }, [dispatch, updateCompanyStatus, t, company]);

  useEffect(() => {
    if (passwordStatus === "success") {
      triggerToast(
        t("users.security.modal.newPassword.success") || "",
        "valid",
      );
      setIsCurrentPasswordCorrect(true);
      setIsModalVisible(false);
      dispatch(UsersSlice.actions.resetUpdatePasswordStatus());
    } else if (passwordStatus === "failed") {
      setIsCurrentPasswordCorrect(false);
      dispatch(UsersSlice.actions.resetUpdatePasswordStatus());
    }
  }, [dispatch, passwordStatus, t]);

  const onSubmitPassword: SubmitHandler<FormPassword> = useCallback(
    (data) => {
      if (data.newPassword === data.confirmedPassword) {
        setIsNewPasswordMatching(true);
        const payload: UsersPasswordsAPI = {
          oldPassword: data.password,
          newPassword: data.newPassword,
        };
        void dispatch(updateUsersPasswordAsync(payload));
      } else return setIsNewPasswordMatching(false);
    },
    [dispatch],
  );

  if (params.tab !== "account" || !company) {
    return;
  }

  return (
    <StyledContainer>
      <Section title={t("users.personalInfo.title") || ""}>
        <Avatar firstName={manager?.firstName} size="L" color="grey" />
        <Spacer y={1.5} />
        <StyledForm
          onSubmit={(event) => void handleSubmit(onSubmitPersonalData)(event)}
        >
          <InputDS
            defaultValue={manager?.firstName}
            {...register("firstName")}
          />
          <InputDS defaultValue={manager?.lastName} {...register("lastName")} />
          <InputDS defaultValue={manager?.email} {...register("email")} />
          <div>
            <ButtonDS
              text={t("users.personalInfo.cta") || ""}
              format={"hug"}
              buttonType={"primary"}
              disabled={buttonDisabled}
            />
          </div>
        </StyledForm>
      </Section>
      <Spacer y={3.5} />
      <Section title={t("users.security.title") || ""}>
        <div>
          <ButtonDS
            text={t("users.security.cta") || ""}
            onClick={() => setIsModalVisible(true)}
            format={"hug"}
            buttonType={"primary"}
          />
        </div>
        <UpdatePasswordModal
          visible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
          }}
          onSubmit={onSubmitPassword}
          isNewPasswordMatching={isNewPasswordMatching}
          isCurrentPasswordCorrect={isCurrentPasswordCorrect}
          passwordStatus={passwordStatus}
        />
      </Section>
      <Spacer y={3.5} />
      <Section title={t(`users.company.status.${company.status}`) || ""}>
        {!isCompanyDeleted && isManagerOwner ? (
          <StyledUpdateCompanyStatus>
            <ButtonDS
              text={t(`users.updateCompanyStatus.cta.${company.status}`) || ""}
              onClick={() => {
                void dispatch(
                  updateCompanyStatusAsync({
                    status: company.status === "ACTIVE" ? "LOCKED" : "ACTIVE",
                  }),
                );
              }}
              format={"hug"}
              buttonType={"primary"}
            />
          </StyledUpdateCompanyStatus>
        ) : (
          ""
        )}
      </Section>
    </StyledContainer>
  );
};

const StyledForm = styled.form`
  width: 100%;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledUpdateCompanyStatus = styled.div`
  display: flex;
  height: 100%;
  justify-content: flex-start;
  align-items: flex-end;
`;
